import { TYPES as APPSTATE_TYPES } from '@monofe/appstate';
import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import type { IDialog, IDialogMessage } from './interface';
import { TYPES as DIALOG_TYPES, bindDialog, injectDialog, unbindDialog } from './interface';
import store from './state';
import type { IModuleState } from './state/interface';
import { constructMessage } from './util/message';
import AlertView from './views/RootView';

function _buildLocalDependencies(container: IContainer) {
    return Promise.resolve({
        store: store(),
    });
}

function _buildModule(deps: { store: IModuleState }): IDialog {
    const Render = (): JSX.Element => {
        const store = deps.store.useState();
        return <AlertView messages={store} />;
    };

    const alert: IDialog = {
        renderer: Render,
        show: function (this: IDialog, message: Partial<IDialogMessage>) {
            const _defaultAction = () => {
                alert.hide(message);
            };

            const newItem = constructMessage(message, {
                onAccept: _defaultAction,
                onDecline: _defaultAction,
            });

            deps.store.action.showPopUp(newItem);
        },
        hide: (message: Partial<IDialogMessage>) => {
            deps.store.action.hidePopUp(message);
        },
        isShowing: (message: Partial<IDialogMessage>) => {
            const results = deps.store
                .getState()
                .filter(item => item.id === message.id)
                .filter(item => item.show ?? true);

            return results.length > 0;
        },
        alert: (id: string) => deps.store.getState().find(item => item.id === id),
        update: (message: Partial<IDialogMessage> & Required<{ id: string }>) => {
            deps.store.action.updatePopUp(message);
        },
    };

    return alert;
}

async function binder(container: IContainer): Promise<Readonly<IDialog>> {
    const deps = await _buildLocalDependencies(container);

    const module = _buildModule(deps);

    return Object.freeze(module);
}

export default buildBindable<IDialog>({
    id: DIALOG_TYPES.IDialog,
    dependencies: [APPSTATE_TYPES.IStoresReducer],
    bind: bindDialog,
    remove: unbindDialog,
    inject: injectDialog,
    build: container => binder(container),
});
